import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Tag, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import type { CheckoutFormData, DiscountValidation } from "@/types/order";

const checkoutSchema = z.object({
  firstName: z.string().min(1, "El nombre es requerido").max(100),
  lastName: z.string().min(1, "Los apellidos son requeridos").max(100),
  email: z.string().email("Email inválido").max(255),
  phone: z.string().min(1, "El teléfono es requerido").max(50),
  shippingAddress: z.string().min(1, "La dirección es requerida").max(500),
  discountCode: z.string().max(50).optional(),
});

interface CheckoutFormProps {
  onSubmit: (data: CheckoutFormData) => Promise<void>;
  isSubmitting: boolean;
  appliedDiscount: DiscountValidation | null;
  onApplyDiscount: (discount: DiscountValidation | null) => void;
}

export function CheckoutForm({ onSubmit, isSubmitting, appliedDiscount, onApplyDiscount }: CheckoutFormProps) {
  const [discountInput, setDiscountInput] = useState("");
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);

  const form = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      shippingAddress: "",
      discountCode: "",
    },
  });

  const handleApplyDiscount = async () => {
    if (!discountInput.trim()) return;
    
    setIsValidatingDiscount(true);
    setDiscountError(null);

    try {
      const { data, error } = await supabase.functions.invoke("validate-discount-code", {
        body: { code: discountInput.trim().toUpperCase() },
      });

      if (error) {
        console.error("Error validating discount:", error);
        setDiscountError("Error al validar el código");
        onApplyDiscount(null);
      } else if (!data.valid) {
        setDiscountError(data.message || "Código no válido");
        onApplyDiscount(null);
      } else {
        onApplyDiscount({
          valid: true,
          discountPercent: data.discountPercent,
          message: data.message,
        });
        form.setValue("discountCode", data.code);
      }
    } catch {
      setDiscountError("Error al validar el código");
      onApplyDiscount(null);
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountInput("");
    setDiscountError(null);
    onApplyDiscount(null);
    form.setValue("discountCode", "");
  };

  const handleFormSubmit = (data: CheckoutFormData) => {
    if (appliedDiscount?.valid) {
      data.discountCode = form.getValues("discountCode");
    }
    onSubmit(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre</FormLabel>
                <FormControl>
                  <Input placeholder="Juan" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Apellidos</FormLabel>
                <FormControl>
                  <Input placeholder="Pérez García" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan@ejemplo.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Teléfono</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+506 8888-8888" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="shippingAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dirección de Envío</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Provincia, cantón, distrito, dirección exacta..."
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Discount Code Section */}
        <div className="space-y-2">
          <Label>Código de Descuento (opcional)</Label>
          {appliedDiscount?.valid ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <Check className="h-5 w-5 text-green-600" />
              <span className="flex-1 text-green-700 dark:text-green-400 font-medium">
                {appliedDiscount.message}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveDiscount}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Ingresa tu código"
                  value={discountInput}
                  onChange={(e) => {
                    setDiscountInput(e.target.value.toUpperCase());
                    setDiscountError(null);
                  }}
                  className="pl-10"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyDiscount}
                disabled={!discountInput.trim() || isValidatingDiscount}
              >
                {isValidatingDiscount ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Aplicar"
                )}
              </Button>
            </div>
          )}
          {discountError && (
            <p className="text-sm text-destructive">{discountError}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Procesando...
            </>
          ) : (
            "Realizar Pedido"
          )}
        </Button>
      </form>
    </Form>
  );
}
